import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, UploadCloud, X, AlertTriangle } from "lucide-react";

// Explicit absolute-path upload endpoint. Uses bare axios so the URL is sent
// as-is to the same origin serving the page (no baseURL prefixing).
const UPLOAD_ENDPOINT = "/api/files/upload";

const inputCls = "w-full bg-[#0F0F0F] border border-white/10 px-4 py-3 text-sm text-white placeholder-neutral-500 focus:border-[#C19B6C] focus:outline-none focus:ring-1 focus:ring-[#C19B6C] transition-colors";
const labelCls = "block text-xs font-semibold tracking-wider text-neutral-400 uppercase mb-2";

export function Field({ label, children, error, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      {children}
      {error && <div className="text-xs text-red-400 mt-1">{error.message}</div>}
    </div>
  );
}
export { inputCls };

const MAX_FILE_MB = 10;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

// Controlled uploader: `value` (array of {name,url,path,size,type}) lives in the parent so files survive Back/Next and review.
export function FileUploader({ kind, multiple = false, accept, value = [], onChange, testId, maxFiles = 10, allowPdf = false }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState([]);
  const files = value;

  const handle = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) { if (e.target) e.target.value = ""; return; }
    setFailed([]);
    const room = multiple ? Math.max(0, maxFiles - files.length) : 1;
    if (selected.length > room) {
      toast.error(room === 0 ? `You already have ${maxFiles} files. Remove one to add another.` : `Only ${room} more file${room === 1 ? "" : "s"} allowed (max ${maxFiles}).`);
    }
    const accepted = [];
    const rejected = [];
    for (const f of selected.slice(0, room)) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      const isImage = IMAGE_TYPES.includes(f.type) || ["jpg", "jpeg", "png", "webp", "heic", "heif"].includes(ext);
      const isPdf = allowPdf && (f.type === "application/pdf" || ext === "pdf");
      if (!isImage && !isPdf) rejected.push({ name: f.name, reason: `Unsupported format. Use JPEG, PNG, WebP or HEIC${allowPdf ? " or PDF" : ""}.` });
      else if (f.size > MAX_FILE_MB * 1024 * 1024) rejected.push({ name: f.name, reason: `Larger than ${MAX_FILE_MB} MB.` });
      else {
        // Android document picker (Drive/Downloads) hands over lazy content:// blobs that can vanish before the request
        // is serialized (ERR_UPLOAD_FILE_CHANGED). Copy into memory first and fill in a missing PDF MIME type.
        try {
          const buf = await f.arrayBuffer();
          accepted.push(new File([buf], f.name, { type: f.type || (ext === "pdf" ? "application/pdf" : ""), lastModified: f.lastModified }));
        } catch {
          rejected.push({ name: f.name, reason: "Could not read this file from your device. Save it locally and try again." });
        }
      }
    }
    if (e.target) e.target.value = "";
    if (rejected.length) setFailed(rejected);
    if (!accepted.length) return;

    const formData = new FormData();
    accepted.forEach((file) => formData.append("files", file));
    formData.append("kind", kind);
    setBusy(true);
    setProgress(0);
    try {
      const res = await axios.post(UPLOAD_ENDPOINT, formData, {
        timeout: 120000,
        withCredentials: true,
        onUploadProgress: (evt) => { if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100)); },
      });
      const returned = Array.isArray(res.data?.files) ? res.data.files : [];
      if (!returned.length) throw new Error("Server returned no files");
      const uploaded = returned.map((x, i) => ({
        name: x.original_filename || accepted[i]?.name || "file",
        url: x.path || x.absolute_url || "",
        path: x.path || "",
        size: x.size || accepted[i]?.size || 0,
        type: x.content_type || accepted[i]?.type || "",
      }));
      onChange?.(multiple ? [...files, ...uploaded] : uploaded);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[FileUploader] Upload error:", err);
      const detail = err?.response?.data?.detail;
      const reason = err?.code === "ECONNABORTED" ? "Upload timed out. Try again on a faster connection."
        : typeof detail === "string" ? detail
        : err?.response?.status ? `Upload failed (HTTP ${err.response.status}). Try again.`
        : `Upload failed before reaching the server${err?.message ? ` (${err.message})` : ""}. Check your connection and try again.`;
      setFailed((prev) => [...prev, ...accepted.map((f) => ({ name: f.name, reason }))]);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const remove = (idx) => onChange?.(files.filter((_, i) => i !== idx));
  const fmtSize = (b) => (!b ? "" : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
  const full = multiple && files.length >= maxFiles;

  return (
    <div>
      <label className={`flex items-center gap-3 border border-dashed px-4 py-4 text-sm transition-colors ${full ? "border-white/10 text-neutral-500 cursor-not-allowed" : "cursor-pointer border-white/15 hover:border-[#C19B6C]/60 text-neutral-300"}`}>
        {busy ? <Loader2 className="animate-spin" size={16} /> : <UploadCloud size={16} className="text-[#C19B6C]" />}
        <span>
          {busy ? `Uploading${progress ? ` ${progress}%` : "…"}` : full ? `Maximum of ${maxFiles} files reached` : multiple ? `Add photos (${files.length}/${maxFiles})` : "Upload file"}
        </span>
        <input type="file" multiple={multiple} accept={accept} onChange={handle} className="hidden" disabled={busy || full} data-testid={testId} />
      </label>
      <p className="text-xs text-neutral-500 mt-2">JPEG, PNG, WebP or HEIC{allowPdf ? " or PDF" : ""}. Up to {MAX_FILE_MB} MB each{multiple ? `, up to ${maxFiles} files` : ""}.</p>
      {files.length > 0 && (
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`${testId}-list`}>
          {files.map((f, i) => (
            <li key={`${f.url}-${i}`} className="flex items-center gap-3 text-xs text-neutral-300 bg-[#0F0F0F] border border-white/10 px-3 py-2" data-testid={`${testId}-item-${i}`}>
              {f.url && !/\.(heic|heif|pdf)$/i.test(f.name) ? (
                <img src={f.url} alt="" className="w-10 h-10 object-cover shrink-0" loading="lazy" />
              ) : (
                <span className="w-10 h-10 shrink-0 bg-white/5 flex items-center justify-center text-[10px] uppercase">{(f.name.split(".").pop() || "file").slice(0, 4)}</span>
              )}
              <span className="truncate flex-1">
                <span className="block truncate">{f.name}</span>
                <span className="text-neutral-500">Uploaded{f.size ? ` · ${fmtSize(f.size)}` : ""}</span>
              </span>
              <button type="button" onClick={() => remove(i)} className="text-neutral-500 hover:text-red-400" aria-label={`Remove ${f.name}`} data-testid={`${testId}-remove-${i}`}><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      {failed.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid={`${testId}-errors`}>
          {failed.map((f, i) => <li key={i} className="text-xs text-red-300">{f.name}: {f.reason}</li>)}
        </ul>
      )}
    </div>
  );
}

export function FormShell({ title, subtitle, children }) {
  return (
    <div className="card-frame p-7 md:p-10">
      <div className="mb-8">
        <h3 className="font-display text-2xl md:text-3xl text-white">{title}</h3>
        {subtitle && <p className="text-sm text-neutral-400 mt-2">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function SubmitBar({ pending, label = "Submit" }) {
  return (
    <div className="flex items-center justify-between gap-4 pt-2">
      <p className="text-xs text-neutral-500 max-w-xs">No spam. No pressure. Your information is used only to evaluate the best next step.</p>
      <button type="submit" disabled={pending} className="btn-gold px-7 py-3.5 font-semibold tracking-wide disabled:opacity-60 inline-flex items-center gap-2" data-testid="form-submit">
        {pending && <Loader2 className="animate-spin" size={16} />} {label}
      </button>
    </div>
  );
}

export function SubmitError({ error, onRetry, pending }) {
  if (!error) return null;
  const fieldList = Object.entries(error.fields || {});
  return (
    <div role="alert" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="submit-error">
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <p>{error.message}</p>
          {fieldList.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-red-200/90">
              {fieldList.map(([k, v]) => <li key={k}><span className="capitalize">{k.replace(/_/g, " ")}</span>: {v}</li>)}
            </ul>
          )}
        </div>
        {error.kind !== "validation" && onRetry && (
          <button type="button" onClick={onRetry} disabled={pending} className="btn-ghost px-3 py-1.5 text-xs whitespace-nowrap disabled:opacity-50" data-testid="submit-retry">
            {pending ? "Retrying…" : "Try again"}
          </button>
        )}
      </div>
    </div>
  );
}

export { useForm, toast };
